import React, { useEffect, useState } from 'react';
import { Box, BoxProps, CircularProgress, Typography } from '@mui/material';
import apiCall from '../../utils/api';

interface SecureImageProps extends BoxProps {
  src?: string | null;
  alt?: string;
}

const SecureImage: React.FC<SecureImageProps> = ({ src, alt, ...props }) => {
  const [imageSrc, setImageSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setError(false);

    const fetchImage = async () => {
      if (!src) {
        setImageSrc(undefined);
        return;
      }

      if (src.startsWith('http') || src.startsWith('blob:') || src.startsWith('data:')) {
        setImageSrc(src);
        return;
      }

      setLoading(true);
      try {
        const response = await apiCall<Blob>(
          'get',
          `/api/fetch-s3-file/?file_path=${encodeURIComponent(src)}&file_format=blob`,
          undefined,
          undefined,
          'blob'
        );

        if (active && response.data instanceof Blob) {
          if (response.data.size > 0) {
            objectUrl = URL.createObjectURL(response.data);
            setImageSrc(objectUrl);
          } else {
            console.warn("SecureImage: Received empty blob for", src);
            setError(true);
          }
        }
      } catch (error) {
        console.error("Failed to fetch secure image:", error);
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchImage();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" bgcolor="#f5f5f5" minHeight={50} {...props}><CircularProgress size={20} /></Box>;
  }

  if (error || !imageSrc) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" bgcolor="#f5f5f5" minHeight={50} {...props}>
        <Typography variant="body2" color="text.secondary">
          {error ? "Failed to load image" : "No Signature Available"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="img" src={imageSrc} alt={alt} {...props} />
  );
};

export default SecureImage;
